import React from "react";
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { Home, Store, LayoutGrid, User, Plus, Sprout, Languages, LogOut, ShieldCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import InstallPrompt from "@/components/InstallPrompt";

const Layout = () => {
  const { t, lang, toggleLang } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;

  const isActive = (p) => (p === "/" ? path === "/" : path.startsWith(p));

  const navItems = [
    { p: "/", label: t("nav.home"), icon: Home, testId: "nav-home" },
    { p: "/market", label: t("nav.market"), icon: Store, testId: "nav-market" },
    { p: "/categories", label: t("nav.categories"), icon: LayoutGrid, testId: "nav-categories" },
    { p: "/profile", label: t("nav.profile"), icon: User, testId: "nav-profile" },
  ];

  const handleLogout = () => {
    logout();
    toast.success(t("toast.logoutSuccess"));
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#F6F7F4] flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <Link to="/" data-testid="header-logo" className="flex items-center gap-2 shrink-0">
            {/* <div className="w-9 h-9 rounded-xl bg-[#123A2C] flex items-center justify-center"> */}
             <img
            src="https://nepalimandi.com/icon-512.png"
            alt={t("app.name")}
            className="w-16 h-12 rounded-xl object-cover"
          />
            {/* </div> */}
            <div className="hidden sm:block">
              <span className="block font-extrabold text-[#14251F] text-lg leading-tight">{t("app.name")}</span>
              <span className="block whitespace-pre-line text-[10px] leading-tight text-[#6B7280]">{t("app.tagline")}</span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navItems.slice(0, 3).map((n) => (
              <button
                key={n.p}
                data-testid={`desktop-${n.testId}`}
                onClick={() => navigate(n.p)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(n.p) ? "text-[#123A2C] bg-[#DCE7E2]" : "text-[#6B7280] hover:text-[#123A2C]"
                }`}
              >
                {n.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              data-testid="lang-toggle"
              onClick={toggleLang}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E5E7EB] text-sm font-medium text-[#14251F] hover:bg-[#F6F7F4] transition-colors"
            >
              <Languages className="w-4 h-4" />
              {lang === "ne" ? "EN" : "नेपाली"}
            </button>

            {user ? (
              <div className="flex items-center gap-2">
                {user.role === "seller" && (
                  <button
                    data-testid="header-dashboard"
                    onClick={() => navigate("/dashboard")}
                    className="hidden sm:inline-flex px-3 py-1.5 rounded-full bg-[#123A2C] text-white text-sm font-medium hover:bg-[#0D2C21] transition-colors"
                  >
                    {t("nav.dashboard")}
                  </button>
                )}
                {user.role === "admin" && (
                  <button
                    data-testid="header-admin"
                    onClick={() => navigate("/admin")}
                    className="hidden sm:inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#123A2C] text-white text-sm font-medium hover:bg-[#0D2C21] transition-colors"
                  >
                    <ShieldCheck className="w-4 h-4" /> {t("nav.admin")}
                  </button>
                )}
                <button
                  data-testid="header-logout"
                  onClick={handleLogout}
                  className="p-2 rounded-full text-[#6B7280] hover:text-[#DC2626] hover:bg-[#F6F7F4] transition-colors"
                  title={t("nav.logout")}
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <>
                <button
                  data-testid="header-login"
                  onClick={() => navigate("/login")}
                  className="px-3 py-1.5 rounded-full text-sm font-medium text-[#123A2C] hover:bg-[#F6F7F4] transition-colors"
                >
                  {t("nav.login")}
                </button>
                <button
                  data-testid="header-signup"
                  onClick={() => navigate("/signup")}
                  className="px-3 py-1.5 rounded-full bg-[#123A2C] text-white text-sm font-medium hover:bg-[#0D2C21] transition-colors"
                >
                  {t("nav.signup")}
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 w-full pb-24 md:pb-8">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#E5E7EB]">
        <div className="grid grid-cols-4 max-w-md mx-auto">
          {navItems.map((n) => {
            const Icon = n.icon;
            const active = isActive(n.p);
            return (
              <button
                key={n.p}
                data-testid={`mobile-${n.testId}`}
                onClick={() => navigate(n.p)}
                className="flex flex-col items-center justify-center py-2.5 gap-0.5"
              >
                <Icon className={`w-6 h-6 ${active ? "text-[#123A2C]" : "text-[#9A9E97]"}`} />
                <span className={`text-[11px] ${active ? "text-[#123A2C] font-semibold" : "text-[#9A9E97]"}`}>
                  {n.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Seller floating add button (mobile) */}
      {user?.role === "seller" && (
        <button
          data-testid="fab-add-product"
          onClick={() => navigate("/dashboard/add")}
          className="md:hidden fixed bottom-20 right-4 z-40 w-14 h-14 rounded-full bg-[#123A2C] text-white flex items-center justify-center shadow-lg hover:bg-[#0D2C21] transition-colors"
        >
          <Plus className="w-7 h-7" />
        </button>
      )}

      <InstallPrompt />
    </div>
  );
};

export default Layout;
